// Geo-aware free job-board directory + a light location→country resolver.
// Used by the recruiter "Distribute" dialog to help post a job to the free
// boards relevant to the JD's location (Google/Bing are automatic via the
// public job page's structured data + feed).

const COUNTRY_KEYWORDS = {
  US: ['united states', 'usa', 'u.s', 'america', 'new york', 'san francisco', 'california', 'texas', 'seattle', 'austin', 'boston', 'chicago', 'los angeles', 'atlanta', 'denver'],
  IN: ['india', 'bengaluru', 'bangalore', 'mumbai', 'delhi', 'hyderabad', 'pune', 'chennai', 'gurgaon', 'gurugram', 'noida', 'kolkata', 'ahmedabad'],
  GB: ['united kingdom', 'u.k', 'uk', 'london', 'manchester', 'england', 'scotland', 'wales', 'birmingham', 'leeds'],
  AE: ['uae', 'united arab emirates', 'dubai', 'abu dhabi', 'sharjah'],
  SG: ['singapore'],
  CA: ['canada', 'toronto', 'vancouver', 'montreal', 'ottawa', 'calgary'],
  AU: ['australia', 'sydney', 'melbourne', 'brisbane', 'perth'],
  DE: ['germany', 'deutschland', 'berlin', 'munich', 'münchen', 'frankfurt', 'hamburg'],
};

const COUNTRY_NAMES = {
  US: 'United States', IN: 'India', GB: 'United Kingdom', AE: 'United Arab Emirates',
  SG: 'Singapore', CA: 'Canada', AU: 'Australia', DE: 'Germany', GLOBAL: 'Global / Remote',
};

export function resolveCountry(location) {
  const loc = (location || '').toLowerCase();
  for (const [code, keys] of Object.entries(COUNTRY_KEYWORDS)) {
    if (keys.some((k) => loc.includes(k))) return { code, name: COUNTRY_NAMES[code] };
  }
  return { code: 'GLOBAL', name: COUNTRY_NAMES.GLOBAL };
}

// Boards that pick jobs up automatically (no manual posting needed).
export const AUTOMATIC_BOARDS = [
  { name: 'Google for Jobs', url: 'https://search.google.com/search-console', note: 'Auto-indexed from your public job page (JobPosting structured data). Submit your sitemap in Search Console.' },
  { name: 'Bing Jobs', url: 'https://www.bing.com/webmasters', note: 'Auto-indexed from structured data — verify your site in Bing Webmaster Tools.' },
];

const GLOBAL_BOARDS = [
  { name: 'LinkedIn (1 free job)', url: 'https://www.linkedin.com/jobs/post/', note: 'Free: one open job at a time' },
  { name: 'Indeed', url: 'https://employers.indeed.com/', note: 'Free posting tier (sponsor for more reach)' },
  { name: 'Jora', url: 'https://employer.jora.com/', note: 'Free global aggregator' },
];

const REMOTE_BOARDS = [
  { name: 'Remotive', url: 'https://remotive.com/employers', note: 'Remote roles' },
  { name: 'We Work Remotely', url: 'https://weworkremotely.com/remote-jobs/new', note: 'Remote roles' },
];

const COUNTRY_BOARDS = {
  US: [
    { name: 'Wellfound (AngelList)', url: 'https://wellfound.com/recruit', note: 'Free for startups' },
    { name: 'Handshake', url: 'https://joinhandshake.com/employers/', note: 'Students & new grads' },
    { name: 'Indeed US', url: 'https://employers.indeed.com/', note: 'Free tier' },
  ],
  IN: [
    { name: 'Apna', url: 'https://apna.co/', note: 'Free job posting' },
    { name: 'WorkIndia', url: 'https://www.workindia.in/employer/', note: 'Free tier' },
    { name: 'Freshersworld', url: 'https://www.freshersworld.com/recruiter', note: 'Free for entry-level' },
    { name: 'Indeed India', url: 'https://employers.indeed.com/', note: 'Free tier' },
  ],
  GB: [
    { name: 'Adzuna UK', url: 'https://www.adzuna.co.uk/', note: 'Aggregator — pulls from your feed' },
    { name: 'Indeed UK', url: 'https://employers.indeed.com/', note: 'Free tier' },
  ],
  AE: [
    { name: 'Laimoon', url: 'https://laimoon.com/', note: 'Free tier' },
    { name: 'Naukrigulf', url: 'https://www.naukrigulf.com/recruit/', note: 'Free tier' },
    { name: 'Indeed UAE', url: 'https://employers.indeed.com/', note: 'Free tier' },
  ],
  SG: [
    { name: 'MyCareersFuture', url: 'https://www.mycareersfuture.gov.sg/', note: 'Free (government)' },
    { name: 'JobStreet SG', url: 'https://www.jobstreet.com.sg/en/cms/employer/', note: 'Free tier' },
    { name: 'Indeed SG', url: 'https://employers.indeed.com/', note: 'Free tier' },
  ],
  CA: [
    { name: 'Job Bank Canada', url: 'https://www.jobbank.gc.ca/employers', note: 'Free (government)' },
    { name: 'Indeed Canada', url: 'https://employers.indeed.com/', note: 'Free tier' },
  ],
  AU: [
    { name: 'Workforce Australia', url: 'https://www.workforceaustralia.gov.au/businesses', note: 'Free (government)' },
    { name: 'Jora AU', url: 'https://au.jora.com/employer', note: 'Free aggregator' },
    { name: 'Indeed AU', url: 'https://employers.indeed.com/', note: 'Free tier' },
  ],
  DE: [
    { name: 'Bundesagentur für Arbeit', url: 'https://www.arbeitsagentur.de/', note: 'Free (government)' },
    { name: 'Adzuna DE', url: 'https://www.adzuna.de/', note: 'Aggregator — pulls from your feed' },
    { name: 'Indeed DE', url: 'https://employers.indeed.com/', note: 'Free tier' },
  ],
};

// Returns the free boards to show for a given country code, plus globals.
export function boardsForCountry(code) {
  const specific = COUNTRY_BOARDS[code] || REMOTE_BOARDS;
  return [...specific, ...GLOBAL_BOARDS];
}
